import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Moment from "moment";
import { Modal, Button, Dropdown } from "react-bootstrap";
import { baseurladdress } from "../baseurl";
import Sidennav from "../dashboard/sidenavbar";
import Topnavbar from "../dashboard/topnavbar";
import "datatables.net-dt/js/dataTables.dataTables";
import "datatables.net-dt/css/jquery.dataTables.min.css";
import $ from 'jquery';
import '../index.css';

const STATUS_PENDING = 1;
const STATUS_APPROVED = 2;
const STATUS_CANCELLED = 3;
const STATUS_RECOMMENDED = 4;

const toNumber = (value, fallback = NaN) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const normalizeRoleKey = (value) => normalizeText(value).replace(/\s+/g, "");

const includesAny = (value, terms) => {
    const text = normalizeText(value);
    return (Array.isArray(terms) ? terms : []).some((term) => text.includes(term));
};

const isRoleTextMatch = (value, terms) => {
    const text = normalizeText(value);
    if (!text) return false;
    return (Array.isArray(terms) ? terms : []).some((term) => text.includes(normalizeText(term)));
};

const findApprovalLevelByRole = (approvalLevels, roleText) => {
    const roleKey = normalizeRoleKey(roleText);
    if (!roleKey || !Array.isArray(approvalLevels)) return null;

    return approvalLevels.find((item) => normalizeRoleKey(item?.Llevel_rolename) === roleKey) || null;
};

const findApprovalLevelByRoleName = (approvalLevels, roleText) => {
    const normalizedSearch = normalizeText(roleText);
    if (!normalizedSearch || !Array.isArray(approvalLevels)) return null;

    return approvalLevels.find((item) => {
        const itemRoleName = normalizeText(item?.Llevel_rolename);
        return itemRoleName.includes(normalizedSearch) || normalizedSearch.includes(itemRoleName);
    }) || null;
};

const findApprovalLevelById = (approvalLevels, levelId) => {
    const id = String(levelId ?? "").trim();
    if (!id || !Array.isArray(approvalLevels)) return null;

    return approvalLevels.find((item) => String(item?.Llevel_id ?? "").trim() === id) || null;
};

const resolveConfiguredApprovalPriority = (
    approvalLevels,
    roleText,
    fallbackPriority = Number.POSITIVE_INFINITY
) => {
    const matchedLevel = findApprovalLevelByRole(approvalLevels, roleText);
    const configuredPriority = toNumber(matchedLevel?.Llevel_priority, NaN);

    if (Number.isFinite(configuredPriority)) {
        return configuredPriority;
    }

    return toNumber(fallbackPriority, resolveApprovalPriority(roleText, fallbackPriority));
};

const resolveApprovalPriority = (roleText, fallbackPriority = Number.POSITIVE_INFINITY) => {
    const text = normalizeText(roleText);

    if (!text) {
        return toNumber(fallbackPriority, Number.POSITIVE_INFINITY);
    }

    if (text.includes("branch head") || text.includes("reporting manager") || text.includes("branch manager") || text.includes("head office")) {
        return 5;
    }

    if (text.includes("cluster")) {
        return 4;
    }

    if (
        text.includes("technical") ||
        text.includes("operation") ||
        text.includes("quality") ||
        text.includes("technical manager") ||
        text.includes("operation manager") ||
        text.includes("quality manager")
    ) {
        return 3;
    }

    if (text.includes("hr")) {
        return 2;
    }

    if (text.includes("coo") || text.includes("chief officer") || text.includes("chief operating officer") || text.includes("final approver")) {
        return 1;
    }

    return toNumber(fallbackPriority, Number.POSITIVE_INFINITY);
};

const pickFirst = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }
    return "";
};

const toIdList = (value) => {
    if (Array.isArray(value)) {
        return value
            .flatMap((item) => {
                if (item === undefined || item === null) return [];
                if (typeof item === "object") {
                    return [
                        item.branch_id,
                        item.id,
                        item.value,
                        item.staff_branch_id,
                        item.user_staffbranch_id,
                    ];
                }
                return [item];
            })
            .map((item) => String(item).trim())
            .filter(Boolean);
    }

    if (value === undefined || value === null) {
        return [];
    }

    const text = String(value).trim();
    if (!text) {
        return [];
    }

    if (text.includes(",")) {
        return text
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return [text];
};

const uniqueIds = (values) => [...new Set(values.map((item) => String(item).trim()).filter(Boolean))];

const getBranchIdsFromRecord = (record) =>
    uniqueIds([
        ...toIdList(record?.user_staffbranch),
        ...toIdList(record?.user_branches),
        ...toIdList(record?.staff_branchbranch),
        ...toIdList(record?.staff_branch_id),
        ...toIdList(record?.staff_branchids),
        ...toIdList(record?.branch_ids),
        ...toIdList(record?.branch_id),
    ]);

const getDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getLeaveDays = (item) => {
    const fromDate = getDateValue(
        pickFirst(
            item?.ddchrleave_appliedfordate,
            item?.leave_from,
            item?.from_date,
            item?.start_date
        )
    );
    const toDate = getDateValue(
        pickFirst(
            item?.ddchrleave_appliedtodate,
            item?.leave_to,
            item?.to_date,
            item?.end_date
        )
    );

    if (!fromDate || !toDate) {
        return toNumber(item?.leave_days, 0);
    }

    const diffTime = Math.abs(toDate - fromDate);
    return Math.max(Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1, 1);
};

const getItemBranchId = (item) =>
    pickFirst(
        item?.ddchrleave_branchid,
        item?.branch_id,
        item?.leave_branchid,
        item?.staff_branchid,
        item?.staff_placedat_branchid
    );

const getItemBranchName = (item) =>
    pickFirst(
        item?.ddchrleave_branchname,
        item?.branch_name,
        item?.leave_branchname,
        item?.staff_branch_name
    );

const getItemStaffId = (item) =>
    pickFirst(
        item?.ddchrleave_staffid,
        item?.staff_id,
        item?.leave_staffid
    );

const getRecipientStaffId = (item) =>
    pickFirst(
        item?.active_recommended_to_staffid,
        item?.last_recommended_to_staffid,
        item?.recommended_to_staffid,
        item?.hierarchy_uptdby,
        item?.recommended_staffid,
        item?.last_recommended_to_userid
    );

const getRecipientLevelId = (item) =>
    pickFirst(
        item?.active_recommended_to_level_id,
        item?.last_recommended_to_level_id,
        item?.recommended_to_level_id,
        item?.hierarchy_levelid,
        item?.recommended_level_id,
        item?.last_recommended_to_levelid
    );

const getRecipientLevelName = (item) =>
    pickFirst(
        item?.active_recommended_to_level_name,
        item?.last_recommended_to_level_name,
        item?.recommended_to_level_name,
        item?.hierarchy_levelname,
        item?.recommended_level_name,
        item?.last_recommended_to_levelname
    );

const getNextRecommendationRolePatterns = (approverRoleText, isLongLeave = false) => {
    const roleText = normalizeText(approverRoleText);

    if (roleText.includes("branch head") || roleText.includes("reporting manager") || roleText.includes("branch manager")) {
        return ["cluster head", "department head"];
    }

    if (roleText.includes("cluster head") || roleText.includes("department head")) {
        return ["quality head", "quality manager", "operation head", "operations head", "operation manager", "technical head", "technical manager"];
    }

    if (
        roleText.includes("quality head") ||
        roleText.includes("quality manager") ||
        roleText.includes("operation head") ||
        roleText.includes("operations head") ||
        roleText.includes("operation manager") ||
        roleText.includes("technical head") ||
        roleText.includes("technical manager") ||
        roleText.includes("region head") ||
        roleText.includes("regional head") ||
        roleText.includes("region")
    ) {
        // Quality/Operation/Technical/Region Head can recommend to:
        // - COO (their next level authority)
        // - Other Quality/Operation/Technical heads at the same level (for cross-verification if needed)
        return ["coo", "chief operating officer", "quality head", "quality manager", "operation head", "operations head", "operation manager", "technical head", "technical manager"];
    }

    return isLongLeave ? ["coo", "chief operating officer"] : [];
};

const isTargetRoleAllowed = (targetRoleText, rolePatterns) => {
    const normalizedRoleText = normalizeText(targetRoleText);
    if (!normalizedRoleText || !Array.isArray(rolePatterns) || rolePatterns.length === 0) {
        return false;
    }

    return rolePatterns.some((pattern) => normalizedRoleText.includes(normalizeText(pattern)));
};

function Leavelist() {
    const Swal = require("sweetalert2");
    const accesstoken = localStorage.getItem("token");
    const loginStaffId = localStorage.getItem("staffid");
    const loginUserId = localStorage.getItem("id");
    const loginRoleId = localStorage.getItem("roleid");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noDataFound, setNoDataFound] = useState(false);
    const [metadataLoaded, setMetadataLoaded] = useState(false);

    const [ddchrleave_id, setddchrleave_id] = useState("");
    const [activeLeaveItem, setActiveLeaveItem] = useState(null);

    const [showModals, setShows] = useState(false);
    const [staffname, setstaffname] = useState("");
    const [leavedate, setleavedate] = useState("");
    const [leavedateto, setleavedateto] = useState("");
    const [status, setstatus] = useState("");
    const [reason, setreason] = useState("");
    const [periodFrom, setPeriodFrom] = useState("");
    const [periodTo, setPeriodTo] = useState("");
    const [statusOptions, setStatusOptions] = useState([]);
    const [recommendToStaffId, setRecommendToStaffId] = useState("");
    const [recommendTargets, setRecommendTargets] = useState([]);
    const [showRecommendPopup, setShowRecommendPopup] = useState(false);

    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [page, setPage] = useState(0);
    const pageSize = 10;
    const [selectedBranchIds, setSelectedBranchIds] = useState([]);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const tableRef = useRef(null);
    const dataTableInitialized = useRef(false);

    // Bulk selection state
    const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);
    const [bulkStatus, setBulkStatus] = useState("");
    const [bulkRecommendToStaffId, setBulkRecommendToStaffId] = useState("");
    const [bulkRecommendTargets, setBulkRecommendTargets] = useState([]);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    const getBranchId = (branch) => branch?.branch_id ?? branch?.branchid ?? null;
    const getBranchCode = (branch) => branch?.branch_code ?? branch?.branchcode ?? '';
    const getBranchName = (branch) => branch?.branch_name ?? branch?.branchname ?? '';

    const branchSelectorStyles = {
        wrapper: {
            position: "relative"
        },
        trigger: {
            minHeight: "38px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            backgroundColor: "#ffffff",
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 12px",
            cursor: "pointer"
        },
        triggerText: {
            fontSize: "0.95rem",
            color: "#52637d",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingRight: "12px"
        },
        triggerIcon: {
            fontSize: "0.9rem",
            color: "#70819b",
            flexShrink: 0
        },
        dropdown: {
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 20,
            border: "1px solid #ced4da",
            borderRadius: "4px",
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 18px rgba(31, 45, 61, 0.12)",
            overflow: "hidden"
        },
        header: {
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            background: "linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)",
            borderBottom: "1px solid #e4ebf5"
        },
        headerText: {
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#4b5b76"
        },
        list: {
            maxHeight: "190px",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "6px"
        },
        item: {
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            padding: "6px 10px",
            marginBottom: "4px",
            borderRadius: "2px",
            cursor: "pointer",
            transition: "background-color 0.2s ease"
        },
        checkbox: {
            marginTop: "2px",
            width: "14px",
            height: "14px",
            accentColor: "#2f5bea",
            flexShrink: 0
        },
        label: {
            marginBottom: 0,
            fontSize: "0.92rem",
            fontWeight: 400,
            color: "#2f3b52",
            lineHeight: "1.2",
            wordBreak: "break-word"
        }
    };

    const [branches, setBranches] = useState([]);
    const [regions, setRegions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [approvalLevels, setApprovalLevels] = useState([]);
    const [staffDirectory, setStaffDirectory] = useState([]);
    const [currentStaffRecord, setCurrentStaffRecord] = useState(null);
    const [currentUserRecord, setCurrentUserRecord] = useState(null);

    const showToast = (icon, title) => {
        Swal.fire({
            toast: true,
            position: "top-end",
            icon,
            title,
            showConfirmButton: false,
            timer: 2200,
            timerProgressBar: true,
        });
    };

    const fetchLeaveList = async () => {
        if (!loginStaffId) {
            setData([]);
            setNoDataFound(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        const queryParams = {};
        if (periodFrom) queryParams.from = periodFrom;
        if (periodTo) queryParams.to = periodTo;

        try {
            const queryString = new URLSearchParams(queryParams).toString();
            const res = await axios.get(
                `${baseurladdress}leavelistbyapprover/${loginStaffId}${queryString ? `?${queryString}` : ""}`,
                { headers: { Authorization: `Bearer ${accesstoken}` } }
            );

            const rows = Array.isArray(res.data?.response) ? res.data.response : [];
            setData(rows);
            setNoDataFound(rows.length === 0);
        } catch (error) {
            console.error("Failed to load leave list:", error);
            setData([]);
            setNoDataFound(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchMetadata = async () => {
        if (!loginStaffId) {
            return;
        }

        try {
            const [staffRes, userRes, currentUserRes, branchRes, roleRes, regionRes, levelRes, statusRes] =
                await Promise.all([
                    axios.get(`${baseurladdress}findallstaff`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    axios.get(`${baseurladdress}findallusers`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    loginUserId
                        ? axios.get(`${baseurladdress}edituser/${loginUserId}`, {
                            headers: { Authorization: `Bearer ${accesstoken}` },
                        })
                        : Promise.resolve({ data: { response: [] } }),
                    axios.get(`${baseurladdress}findallbranches`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    axios.get(`${baseurladdress}findallactiveroles`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    axios.get(`${baseurladdress}getallregions`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    axios.get(`${baseurladdress}getallapprovallevels`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                    axios.get(`${baseurladdress}getallleavestatus`, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    }),
                ]);

            const staffRows = Array.isArray(staffRes.data?.response) ? staffRes.data.response : [];
            const userRows = Array.isArray(userRes.data?.response) ? userRes.data.response : [];
            const currentUserRows = Array.isArray(currentUserRes.data?.response) ? currentUserRes.data.response : [];
            const branchRows = Array.isArray(branchRes.data?.response) ? branchRes.data.response : [];
            const roleRows = Array.isArray(roleRes.data?.response) ? roleRes.data.response : [];
            const regionRows = Array.isArray(regionRes.data?.response) ? regionRes.data.response : [];
            const levelRows = Array.isArray(levelRes.data?.response) ? levelRes.data.response : [];
            const statusRows = Array.isArray(statusRes.data?.response) ? statusRes.data.response : [];

            setBranches(branchRows);
            setRoles(roleRows);
            setRegions(regionRows);
            setApprovalLevels(levelRows);
            setStaffDirectory(staffRows);
            setStatusOptions(statusRows);
            setCurrentUserRecord(
                currentUserRows[0] ||
                userRows.find((item) => String(item?.user_staffid) === String(loginStaffId)) ||
                userRows.find((item) => String(item?.user_id) === String(loginUserId)) ||
                null
            );

            const loginStaff = staffRows.find(
                (item) => String(item?.staff_id) === String(loginStaffId)
            );
            setCurrentStaffRecord(loginStaff || null);
            setMetadataLoaded(true);
        } catch (error) {
            console.error("Failed to load leave metadata:", error);
            setBranches([]);
            setRoles([]);
            setRegions([]);
            setApprovalLevels([]);
            setStaffDirectory([]);
            setStatusOptions([]);
            setCurrentStaffRecord(null);
            setCurrentUserRecord(null);
            setMetadataLoaded(true);
        }
    };

    useEffect(() => {
        fetchMetadata();
    }, [loginStaffId, accesstoken]);

    useEffect(() => {
        fetchLeaveList();
    }, [periodFrom, periodTo, loginStaffId, accesstoken]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowBranchDropdown(false);
            }
        };

        if (showBranchDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [showBranchDropdown]);

    const branchLookup = useMemo(() => {
        return new Map(branches.map((item) => [String(item.branch_id), item]));
    }, [branches]);

    const branchNameLookup = useMemo(() => {
        const map = new Map();
        branches.forEach((item) => {
            const branchName = normalizeText(getBranchName(item));
            if (branchName) {
                map.set(branchName, item);
            }
        });
        return map;
    }, [branches]);

    const regionLookup = useMemo(() => {
        return new Map(regions.map((item) => [String(item.region_id), item]));
    }, [regions]);

    const roleLookup = useMemo(() => {
        const map = new Map();
        roles.forEach((item) => {
            map.set(String(item.role_id), item);
            map.set(normalizeText(item.role_name), item);
        });
        return map;
    }, [roles]);

    const staffLookup = useMemo(() => {
        const map = new Map();
        staffDirectory.forEach((item) => {
            map.set(String(item?.staff_id), item);
        });
        return map;
    }, [staffDirectory]);

    const currentStaffRoleId = String(
        pickFirst(
            currentStaffRecord?.staff_role,
            currentStaffRecord?.role_id,
            currentUserRecord?.user_role,
            loginRoleId,
        ) || ""
    );

    const currentUserRoleId = String(
        pickFirst(
            currentUserRecord?.user_role,
            loginRoleId,
            currentStaffRoleId
        ) || ""
    );

    const currentStaffRoleName = normalizeText(pickFirst(
        roleLookup.get(currentStaffRoleId)?.role_name,
        roleLookup.get(currentUserRoleId)?.role_name,
        currentStaffRecord?.role_name,
        currentStaffRecord?.staff_role_name,
        currentUserRecord?.role_name,
        currentUserRecord?.user_role_name
    ));

    const currentRecordBranchIds = useMemo(() => uniqueIds([
        ...getBranchIdsFromRecord(currentStaffRecord),
        ...getBranchIdsFromRecord(currentUserRecord),
        ...toIdList(currentStaffRecord?.staff_placedat),
    ]), [currentStaffRecord, currentUserRecord]);

    const currentStaffBranchId = String(
        pickFirst(
            currentStaffRecord?.staff_placedat,
            currentStaffRecord?.branch_id,
            currentRecordBranchIds.length === 1 ? currentRecordBranchIds[0] : ""
        ) || ""
    );

    const currentUserBranchIds = useMemo(
        () => uniqueIds(toIdList(currentUserRecord?.user_branches)),
        [currentUserRecord]
    );

    const currentBranch = branchLookup.get(currentStaffBranchId) || null;
    const currentBranchRegionId = String(pickFirst(currentBranch?.branch_region, "") || "");
    const currentRegion = regionLookup.get(currentBranchRegionId) || null;

    const currentApproverLevel =
        findApprovalLevelByRole(approvalLevels, currentStaffRoleName) ||
        findApprovalLevelByRole(approvalLevels, roleLookup.get(currentStaffRoleId)?.role_name) ||
        findApprovalLevelByRole(approvalLevels, roleLookup.get(currentUserRoleId)?.role_name) ||
        findApprovalLevelByRole(approvalLevels, currentStaffRecord?.role_name) ||
        findApprovalLevelByRole(approvalLevels, currentStaffRecord?.staff_role_name) ||
        findApprovalLevelByRole(approvalLevels, currentUserRecord?.role_name) ||
        findApprovalLevelByRole(approvalLevels, currentUserRecord?.user_role_name) ||
        // Fallback: use partial matching for case-insensitive role name matching
        findApprovalLevelByRoleName(approvalLevels, currentStaffRoleName) ||
        findApprovalLevelByRoleName(approvalLevels, roleLookup.get(currentStaffRoleId)?.role_name) ||
        findApprovalLevelByRoleName(approvalLevels, roleLookup.get(currentUserRoleId)?.role_name) ||
        findApprovalLevelByRoleName(approvalLevels, currentStaffRecord?.role_name) ||
        findApprovalLevelByRoleName(approvalLevels, currentStaffRecord?.staff_role_name) ||
        findApprovalLevelByRoleName(approvalLevels, currentUserRecord?.role_name) ||
        findApprovalLevelByRoleName(approvalLevels, currentUserRecord?.user_role_name);
    const currentApproverLevelName = pickFirst(
        currentApproverLevel?.Llevel_rolename,
        currentStaffRoleName,
        currentStaffRecord?.role_name,
        currentStaffRecord?.staff_role_name,
        currentStaffRecord?.staff_role
    );
    const currentApproverLevelPriority = resolveConfiguredApprovalPriority(
        approvalLevels,
        currentApproverLevelName,
        currentApproverLevel?.Llevel_priority
    );
    const maxApprovalPriority = approvalLevels.reduce((max, item) => {
        const priority = toNumber(item?.Llevel_priority, Number.NEGATIVE_INFINITY);
        return Number.isFinite(priority) && priority > max ? priority : max;
    }, Number.NEGATIVE_INFINITY);
    const currentRoleText = normalizeText(currentApproverLevelName);
    const staffRoleText = normalizeText(roleLookup.get(currentStaffRoleId)?.role_name);
    const userRoleText = normalizeText(currentUserRecord?.user_role_name);
    
    const isHrRole =
        currentRoleText.includes("hr") ||
        staffRoleText.includes("hr") ||
        userRoleText.includes("hr");
    const isCooRole =
        !isHrRole &&
        (currentRoleText.includes("coo") ||
            currentRoleText.includes("chief operating officer") ||
            currentRoleText.includes("chief officer") ||
            currentRoleText.includes("final approver") ||
            staffRoleText.includes("coo") ||
            staffRoleText.includes("chief operating officer") ||
            staffRoleText.includes("chief officer") ||
            staffRoleText.includes("final approver") ||
            userRoleText.includes("coo") ||
            userRoleText.includes("chief operating officer") ||
            userRoleText.includes("chief officer") ||
            userRoleText.includes("final approver"));
    
    // Debug logging for COO detection
    console.log("=== COO Role Detection Debug ===");
    console.log("currentRoleText:", currentRoleText);
    console.log("staffRoleText:", staffRoleText);
    console.log("userRoleText:", userRoleText);
    console.log("isHrRole:", isHrRole);
    console.log("isCooRole:", isCooRole);
    console.log("currentApproverLevelName:", currentApproverLevelName);
    console.log("===================================");
    const isRegionRole =
        currentRoleText.includes("region") ||
        currentRoleText.includes("cluster") ||
        currentRoleText.includes("quality") ||
        currentRoleText.includes("operation") ||
        currentRoleText.includes("technical") ||
        staffRoleText.includes("cluster") ||
        staffRoleText.includes("quality") ||
        staffRoleText.includes("operation") ||
        staffRoleText.includes("technical") ||
        staffRoleText.includes("region");
    const approvalRoleText = normalizeText(currentApproverLevel?.Llevel_rolename);
    const isHeadLikeRoleText = (value) => {
        const text = normalizeText(value);
        return (
            text.includes("branch head") ||
            text.includes("reporting manager") ||
            text.includes("branch manager") ||
            text.includes("head office") ||
            (text.includes("head") && text.includes("branch"))
        );
    };
    const isBranchHeadRole =
        isHeadLikeRoleText(approvalRoleText) ||
        isHeadLikeRoleText(currentRoleText) ||
        isHeadLikeRoleText(staffRoleText) ||
        (Number.isFinite(currentApproverLevelPriority) &&
            Number.isFinite(maxApprovalPriority) &&
            Number(currentApproverLevelPriority) === Number(maxApprovalPriority));
    const isClusterHeadRole =
        currentRoleText.includes("cluster") ||
        staffRoleText.includes("cluster") ||
        includesAny(currentApproverLevelName, ["cluster head", "department head"]) ||
        includesAny(roleLookup.get(currentStaffRoleId)?.role_name, ["cluster head", "department head"]);
    const isFinalApprover = currentRoleText.includes("final approver") || staffRoleText.includes("final approver");
    const isSeniorRegionRole =
        includesAny(currentApproverLevelName, ["quality", "quality manager", "operation", "operation manager", "technical", "technical manager"]) ||
        includesAny(roleLookup.get(currentStaffRoleId)?.role_name, ["quality", "quality manager", "operation", "operation manager", "technical", "technical manager"]);
    
    // Helper function to check if the leave applicant is a senior region role (Quality/Operation/Technical Head/Manager)
    const isApplicantSeniorRegionRole = (item) => {
        if (!item) return false;
        
        const applicantStaffId = String(getItemStaffId(item) || "");
        if (!applicantStaffId || applicantStaffId === String(loginStaffId || "")) return false;
        
        const applicantStaff = staffDirectory.find(
            (s) => String(s?.staff_id) === applicantStaffId
        );
        
        if (!applicantStaff) return false;
        
        const applicantRoleId = String(
            pickFirst(applicantStaff?.staff_role, applicantStaff?.role_id, "") || ""
        );
        const applicantRoleName = normalizeText(
            pickFirst(
                roleLookup.get(applicantRoleId)?.role_name,
                applicantStaff?.role_name,
                applicantStaff?.staff_role_name
            )
        );
        
        return includesAny(applicantRoleName, ["quality", "quality manager", "operation", "operation manager", "technical", "technical manager"]);
    };

    // Helper function to check if COO can approve/edit a specific leave
    // COO can approve ALL leaves regardless of duration or applicant role (highest authority)
    const canCooApproveLeave = (item) => {
        if (!isCooRole || !item) return false;
        return true;
    };

    const hrSingleAssignedBranchId =
        isHrRole && currentUserBranchIds.length === 1 ? String(currentUserBranchIds[0]) : "";
    const scopeBranchId = String(hrSingleAssignedBranchId || currentStaffBranchId || "");
    const scopeBranch = branchLookup.get(scopeBranchId) || currentBranch;
    const scopeRegionId = String(pickFirst(scopeBranch?.branch_region, currentBranchRegionId) || "");
    const scopeRegion = regionLookup.get(scopeRegionId) || currentRegion;

    const currentAssociatedBranchIds = useMemo(() => {
        return uniqueIds([
            ...currentRecordBranchIds,
            ...currentUserBranchIds,
            currentStaffBranchId,
        ]);
    }, [currentRecordBranchIds, currentStaffBranchId, currentUserBranchIds]);

    const currentRegionBranchIds = useMemo(() => {
        if (!scopeRegionId) {
            return [];
        }

        return branches
            .filter((item) => String(item?.branch_region || "") === String(scopeRegionId || ""))
            .map((item) => String(item?.branch_id || ""))
            .filter(Boolean);
    }, [branches, scopeRegionId]);

    const currentScopeBranchIds = useMemo(() => {
        if (isCooRole) {
            return branches.map((item) => String(item?.branch_id || "")).filter(Boolean);
        }

        if (isClusterHeadRole) {
            return currentAssociatedBranchIds.length > 0
                ? currentAssociatedBranchIds
                : (scopeBranchId ? [String(scopeBranchId)] : []);
        }

        if (isHrRole || isRegionRole || isSeniorRegionRole) {
            return currentRegionBranchIds.length > 0
                ? currentRegionBranchIds
                : (scopeBranchId ? [String(scopeBranchId)] : []);
        }

        if (isBranchHeadRole) {
            return currentAssociatedBranchIds.length > 0
                ? currentAssociatedBranchIds
                : (scopeBranchId ? [String(scopeBranchId)] : []);
        }

        return scopeBranchId ? [String(scopeBranchId)] : [];
    }, [
        branches,
        currentAssociatedBranchIds,
        currentRegionBranchIds,
        scopeBranchId,
        isBranchHeadRole,
        isCooRole,
        isClusterHeadRole,
        isHrRole,
        isRegionRole,
        isSeniorRegionRole,
    ]);

    const showBranchSelector = !isBranchHeadRole;

    const branchOptions = useMemo(() => {
        if (!Array.isArray(branches) || branches.length === 0) {
            return [];
        }

        if (isCooRole) {
            return [...branches].sort((a, b) =>
                normalizeText(a?.branch_name).localeCompare(normalizeText(b?.branch_name))
            );
        }

        if (isClusterHeadRole) {
            const clusterBranchIds = new Set(currentAssociatedBranchIds);
            const clusterBranches = branches.filter((item) =>
                clusterBranchIds.has(String(item?.branch_id || ""))
            );

            if (clusterBranches.length > 0) {
                return clusterBranches.sort((a, b) =>
                    normalizeText(a?.branch_name).localeCompare(normalizeText(b?.branch_name))
                );
            }

            return scopeBranch ? [scopeBranch] : [];
        }

        const allowedBranchIds = new Set(currentScopeBranchIds);
        const scopedBranches = branches.filter((item) => allowedBranchIds.has(String(item?.branch_id || "")));

        if (scopedBranches.length > 0) {
            return scopedBranches.sort((a, b) =>
                normalizeText(a?.branch_name).localeCompare(normalizeText(b?.branch_name))
            );
        }

        return scopeBranch ? [scopeBranch] : [];
    }, [
        branches,
        scopeBranch,
        currentAssociatedBranchIds,
        currentScopeBranchIds,
        isClusterHeadRole,
        isHrRole,
        isCooRole,
    ]);

    const resolveTargetLevelPriority = (target) => {
        const levelId = pickFirst(
            target?.staff_level_id,
            target?.staff_Llevelid,
            target?.LApr_Llevelid,
            target?.level_id
        );

        const matchedLevel = approvalLevels.find(
            (item) => String(item?.Llevel_id) === String(levelId)
        );
        const targetRoleText = pickFirst(
            matchedLevel?.Llevel_rolename,
            target?.staff_level_name,
            target?.staff_level_rolename,
            target?.level_name,
            target?.role_name
        );

        return resolveConfiguredApprovalPriority(
            approvalLevels,
            targetRoleText,
            matchedLevel?.Llevel_priority ??
            target?.staff_level_priority ??
            target?.level_priority
        );
    };

    const resolveTargetBranchId = (target) =>
        String(
            pickFirst(
                target?.staff_placedat,
                target?.branch_id,
                target?.LApr_branchid
            ) || ""
        );

    const resolveTargetBranchIds = (target) =>
        uniqueIds([
            resolveTargetBranchId(target),
            ...toIdList(target?.user_branches),
            ...toIdList(target?.branch_ids),
            ...toIdList(target?.associated_branch_ids),
        ]);

    const resolveTargetBranchName = (target, leaveItem = activeLeaveItem) => {
        const leaveBranchId = String(getItemBranchId(leaveItem || {}) || "");

        if (leaveBranchId) {
            return pickFirst(
                branchLookup.get(leaveBranchId)?.branch_name,
                getItemBranchName(leaveItem || {})
            );
        }

        return pickFirst(
            target?.branch_name,
            target?.staff_branch_name,
            target?.LApr_branchname
        );
    };

    const resolveTargetRegionId = (target) => {
        const targetBranch = branchLookup.get(resolveTargetBranchId(target));
        return String(pickFirst(targetBranch?.branch_region, target?.region_id, "") || "");
    };

    const resolveTargetRegionName = (target) => {
        const targetRegionId = resolveTargetRegionId(target);
        return pickFirst(
            regionLookup.get(String(targetRegionId))?.region_name,
            target?.region_name
        );
    };

    const isRegionLevelRolePattern = (pattern) => {
        const text = normalizeText(pattern);
        return text.includes("quality") || text.includes("operation") || text.includes("technical") || text.includes("region");
    };

    const resolveLocalRecommendTargets = (leaveItem) => {
        if (!leaveItem) {
            return [];
        }

        const isLongLeave = getLeaveDays(leaveItem) > 4;
        const rolePatterns = getNextRecommendationRolePatterns(currentApproverLevelName, isLongLeave);
        
        if (rolePatterns.length === 0) {
            return [];
        }

        // Determine scope: COO = all branches, region-level roles = region branches, else = single branch
        const hasRegionLevelTarget = rolePatterns.some((pattern) => isRegionLevelRolePattern(pattern));
        const hasCooTarget = rolePatterns.some((pattern) => {
            const text = normalizeText(pattern);
            return text.includes("coo") || text.includes("chief operating officer");
        });

        // Get the leave's branch and region.
        // If leave has no branch, fall back to the approver's scope branch.
        const leaveBranchId = String(getItemBranchId(leaveItem) || scopeBranchId || "");
        const leaveBranch = branchLookup.get(leaveBranchId);
        const leaveRegionId = String(pickFirst(leaveBranch?.branch_region, scopeRegionId) || "");
        
        // If we still have no branch or region ID to filter by, show ALL staff that match the role pattern
        const showAllByRoleOnly = !leaveBranchId && !scopeBranchId;

        console.log("=== resolveLocalRecommendTargets ===");
        console.log("Current user role:", currentApproverLevelName);
        console.log("Next role patterns:", rolePatterns);
        console.log("Has region-level target:", hasRegionLevelTarget);
        console.log("Leave branch ID:", leaveBranchId);
        console.log("Leave region ID:", leaveRegionId);
        console.log("Total staff in directory:", staffDirectory.length);

        // Filter staff by role patterns and branch scope
        const filteredStaff = staffDirectory
            .filter((staff) => {
                const staffId = String(staff?.staff_id || "");
                if (!staffId || staffId === String(loginStaffId)) {
                    return false;
                }

                // Check if staff matches any of the required role patterns
                const roleId = String(pickFirst(staff?.staff_role, staff?.role_id, "") || "");
                const staffRoleName = normalizeText(
                    pickFirst(
                        roleLookup.get(roleId)?.role_name,
                        staff?.role_name,
                        staff?.staff_role_name
                    )
                );

                const matchesRole = rolePatterns.some(pattern => {
                    const normalizedPattern = normalizeText(pattern);
                    
                    // Direct match first
                    if (staffRoleName.includes(normalizedPattern)) {
                        return true;
                    }
                    
                    // For region-level roles, match by base keyword (quality/operation/technical)
                    const baseKeyword = normalizedPattern
                        .replace(/head/g, "")
                        .replace(/manager/g, "")
                        .replace(/operations/g, "operation")
                        .trim();
                    
                    if (baseKeyword && (normalizedPattern.includes("head") || normalizedPattern.includes("manager"))) {
                        return staffRoleName.includes(baseKeyword);
                    }
                    
                    return false;
                });

                if (!matchesRole) {
                    console.log(`Staff ${staff?.staff_name} (${staffRoleName}) - Role mismatch`);
                    return false;
                }

                // For region-level approvers (Operation/Technical/Quality Head), check if they're in the correct region
                if (hasRegionLevelTarget) {
                    const staffBranchId = String(pickFirst(staff?.staff_placedat, staff?.branch_id, "") || "");
                    const staffBranch = branchLookup.get(staffBranchId);
                    const staffRegionId = String(pickFirst(staffBranch?.branch_region, "") || "");
                    
                    console.log(`Staff ${staff?.staff_name} (${staffRoleName}) - Branch: ${staffBranchId}, Region: ${staffRegionId}, Leave Region: ${leaveRegionId}`);
                    
                    // If no leave region ID and we are in 'show all by role only' mode, show all matching
                    if (!leaveRegionId) {
                        if (showAllByRoleOnly) {
                            console.log(`  -> No branch/region available - showing by role match only`);
                            return true;
                        }
                        console.log(`  -> No leave region ID, showing all region-level approvers`);
                        return true;
                    }
                    
                    // Region-level approvers should be visible if:
                    // 1. They're in the same region as the leave (their branch is in the leave's region)
                    const inCorrectRegion = staffRegionId === leaveRegionId;
                    
                    // 2. OR the leave's branch is in the approver's scope (for cluster heads with associated branches)
                    const approverScopeIds = currentScopeBranchIds;
                    const leaveBranchInApproverScope = leaveBranchId && approverScopeIds.includes(leaveBranchId);
                    
                    console.log(`  -> In correct region: ${inCorrectRegion}, Leave in approver scope: ${leaveBranchInApproverScope}`);
                    
                    // Show if either condition is true
                    return inCorrectRegion || leaveBranchInApproverScope;
                }

                // For cluster-level targets, check if the leave's branch is in the cluster head's scope
                if (!hasCooTarget && !hasRegionLevelTarget) {
                    // If no branch/region available, show by role match only
                    if (showAllByRoleOnly) {
                        console.log(`Staff ${staff?.staff_name} (${staffRoleName}) - No branch available, showing by role match only`);
                        return true;
                    }
                    
                    const staffBranchIds = [
                        ...toIdList(staff?.user_branches),
                        String(pickFirst(staff?.staff_placedat, staff?.branch_id, "") || ""),
                    ].filter(Boolean);
                    
                    const leaveBranchInStaffScope = leaveBranchId && staffBranchIds.includes(leaveBranchId);
                    
                    console.log(`Staff ${staff?.staff_name} (${staffRoleName}) - Leave branch in scope: ${leaveBranchInStaffScope}`);
                    
                    return leaveBranchInStaffScope;
                }

                return true;
            })
            .map((staff) => {
                const roleId = String(pickFirst(staff?.staff_role, staff?.role_id, "") || "");
                const branchId = String(pickFirst(staff?.staff_placedat, staff?.branch_id, "") || "");

                return {
                    staff_id: staff?.staff_id,
                    staff_name: staff?.staff_name,
                    staff_level_name: pickFirst(
                        roleLookup.get(roleId)?.role_name,
                        staff?.role_name,
                        staff?.staff_role_name
                    ),
                    staff_level_id: roleId,
                    branch_name: pickFirst(
                        branchLookup.get(branchId)?.branch_name,
                        staff?.branch_name
                    ),
                    branch_id: branchId,
                    user_branches: staff?.user_branches,
                };
            })
            .sort((a, b) => {
                const aPriority = resolveTargetLevelPriority(a);
                const bPriority = resolveTargetLevelPriority(b);
                if (aPriority !== bPriority) return aPriority - bPriority;
                return normalizeText(a?.staff_name).localeCompare(normalizeText(b?.staff_name));
            });

        console.log("Filtered targets:", filteredStaff.map(t => ({ name: t.staff_name, role: t.staff_level_name, branch: t.branch_name })));
        console.log("=== End resolveLocalRecommendTargets ===");
        
        return filteredStaff;
    };

    const canViewLongLeaveActions = isHrRole || isCooRole || isFinalApprover;

    const scopedData = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return [];
        }

        // COO can view ALL leaves from all branches/regions
        if (isCooRole) {
            return data.filter((item) => String(getItemStaffId(item) || "") !== String(loginStaffId || ""));
        }

        if (!isHrRole && !isBranchHeadRole && (!currentApproverLevel || !Number.isFinite(currentApproverLevelPriority))) {
            return [];
        }

        const firstApproverPriority = approvalLevels.reduce((max, item) => {
            const priority = toNumber(item?.Llevel_priority, Number.NEGATIVE_INFINITY);
            return Number.isFinite(priority) && priority > max ? priority : max;
        }, Number.NEGATIVE_INFINITY);

        const currentRoleKeys = new Set(
            [
                currentApproverLevel?.Llevel_rolename,
                currentStaffRoleName,
                roleLookup.get(currentStaffRoleId)?.role_name,
                roleLookup.get(currentUserRoleId)?.role_name,
                currentStaffRecord?.role_name,
                currentStaffRecord?.staff_role_name,
                currentUserRecord?.role_name,
                currentUserRecord?.user_role_name,
            ]
                .map(normalizeRoleKey)
                .filter(Boolean)
        );

        const isCurrentRecipient = (item) => {
            const recipientStaffId = String(getRecipientStaffId(item) || "");
            if (recipientStaffId && recipientStaffId === String(loginStaffId || "")) {
                return true;
            }

            const recipientLevelId = String(getRecipientLevelId(item) || "");
            if (
                recipientLevelId &&
                currentApproverLevel?.Llevel_id &&
                recipientLevelId === String(currentApproverLevel.Llevel_id)
            ) {
                return true;
            }

            const recipientLevel = findApprovalLevelById(approvalLevels, recipientLevelId);
            const recipientLevelName = pickFirst(
                recipientLevel?.Llevel_rolename,
                getRecipientLevelName(item)
            );
            const recipientRoleKey = normalizeRoleKey(recipientLevelName);
            if (recipientRoleKey && currentRoleKeys.has(recipientRoleKey)) {
                return true;
            }

            const recipientPriority = resolveConfiguredApprovalPriority(
                approvalLevels,
                recipientLevelName,
                recipientLevel?.Llevel_priority
            );

            return (
                Number.isFinite(recipientPriority) &&
                Number(recipientPriority) === Number(currentApproverLevelPriority)
            );
        };

        const isUnroutedLeave = (item) =>
            !getRecipientStaffId(item) &&
            !getRecipientLevelId(item) &&
            !getRecipientLevelName(item) &&
            !pickFirst(item?.last_recommender_staffid, item?.approver_staffid);

        const isHandledByCurrentStaff = (item) =>
            String(
                pickFirst(
                    item?.ddchrleave_leaveapproverid,
                    item?.leaveapproverid,
                    item?.last_recommender_staffid,
                    item?.approver_staffid
                ) || ""
            ) === String(loginStaffId || "");

        const isPreviousApprovalLevel = (item) => {
            if (Number(item?.viewer_in_hierarchy) === 1) {
                return true;
            }

            const recipientLevelId = getRecipientLevelId(item);
            const recipientLevel = findApprovalLevelById(approvalLevels, recipientLevelId);
            const recipientPriority = resolveConfiguredApprovalPriority(
                approvalLevels,
                pickFirst(recipientLevel?.Llevel_rolename, getRecipientLevelName(item)),
                recipientLevel?.Llevel_priority
            );

            return (
                Number.isFinite(recipientPriority) &&
                Number.isFinite(currentApproverLevelPriority) &&
                Number(currentApproverLevelPriority) > Number(recipientPriority)
            );
        };

        return data.filter((item) => {
            if (String(getItemStaffId(item) || "") === String(loginStaffId || "")) {
                return false;
            }

            const itemBranchId = String(getItemBranchId(item) || "");
            const itemBranch =
                branchLookup.get(itemBranchId) ||
                branchNameLookup.get(normalizeText(getItemBranchName(item)));
            
            // Check if the leave applicant is an approver - if so, their higher-level supervisors can see it
            const applicantStaffId = String(getItemStaffId(item) || "");
            if (applicantStaffId && applicantStaffId !== String(loginStaffId || "")) {
                const applicantStaff = staffDirectory.find(
                    (s) => String(s?.staff_id) === applicantStaffId
                );
                
                if (applicantStaff) {
                    const applicantRoleId = String(
                        pickFirst(applicantStaff?.staff_role, applicantStaff?.role_id, "") || ""
                    );
                    const applicantRoleName = pickFirst(
                        roleLookup.get(applicantRoleId)?.role_name,
                        applicantStaff?.role_name,
                        applicantStaff?.staff_role_name
                    );
                    
                    // Find the applicant's approval level
                    const applicantApprovalLevel =
                        findApprovalLevelByRole(approvalLevels, applicantRoleName) ||
                        findApprovalLevelByRole(approvalLevels, roleLookup.get(applicantRoleId)?.role_name) ||
                        findApprovalLevelByRole(approvalLevels, applicantStaff?.role_name) ||
                        findApprovalLevelByRole(approvalLevels, applicantStaff?.staff_role_name);
                    
                    if (applicantApprovalLevel && Number.isFinite(currentApproverLevelPriority)) {
                        const applicantPriority = resolveConfiguredApprovalPriority(
                            approvalLevels,
                            applicantApprovalLevel?.Llevel_rolename,
                            applicantApprovalLevel?.Llevel_priority
                        );
                        
                        // If current user has higher priority (lower number = higher level), they can see the leave
                        if (
                            Number.isFinite(applicantPriority) &&
                            Number(currentApproverLevelPriority) < Number(applicantPriority)
                        ) {
                            // Special case: Quality/Operation/Technical Head leaves are visible to HR and COO
                            const applicantIsSeniorRegion = includesAny(
                                applicantApprovalLevel?.Llevel_rolename,
                                ["quality", "quality manager", "operation", "operation manager", "technical", "technical manager"]
                            );
                            
                            if (applicantIsSeniorRegion && (isHrRole || isCooRole)) {
                                return true;
                            }
                            
                            // For other approvers, their highest level can see their leaves
                            if (!applicantIsSeniorRegion) {
                                return true;
                            }
                        }
                    }
                }
            }
            
            // For HR: check if leave's branch is in the same region as HR's region
            if (isHrRole) {
                const itemRegionId = String(
                    pickFirst(
                        itemBranch?.branch_region,
                        item?.branch_region,
                        item?.region_id,
                        item?.ddchrleave_regionid,
                        item?.leave_regionid,
                        ""
                    ) || ""
                );
                const hrRegionId = String(scopeRegionId || "");
                
                // HR can see all leaves from all branches in their region
                if (hrRegionId && itemRegionId === hrRegionId) {
                    return true;
                }
                // HR cannot see leaves from other regions or leaves without a resolvable region
                return false;
            }
            
            // For other roles (non-HR), use the standard branch scope check
            if (
                currentScopeBranchIds.length > 0 &&
                itemBranchId &&
                !currentScopeBranchIds.includes(itemBranchId)
            ) {
                return false;
            }

            const statusCode = Number(item?.viewer_status ?? item?.ddchrleave_status);
            if (isBranchHeadRole) {
                return true;
            }

            if (statusCode === STATUS_PENDING && isUnroutedLeave(item)) {
                return Number(currentApproverLevelPriority) === Number(firstApproverPriority);
            }

            if (statusCode === STATUS_RECOMMENDED) {
                // COO can see ALL recommended leaves from any applicant (their highest authority)
                if (isCooRole) return true;
                return isCurrentRecipient(item) || isHandledByCurrentStaff(item) || isPreviousApprovalLevel(item);
            }

            return isHandledByCurrentStaff(item) || isCurrentRecipient(item) || isPreviousApprovalLevel(item);
        });
    }, [
        approvalLevels,
        branchLookup,
        branchNameLookup,
        currentApproverLevel,
        currentApproverLevelPriority,
        currentScopeBranchIds,
        currentStaffRecord,
        currentStaffRoleId,
        currentStaffRoleName,
        currentUserRecord,
        currentUserRoleId,
        data,
        isBranchHeadRole,
        isCooRole,
        isHrRole,
        loginStaffId,
        roleLookup,
        scopeRegionId,
    ]);

    const buildStatusOptions = (leaveDays = 0) => {
        const base = Array.isArray(statusOptions) ? [...statusOptions] : [];
        const map = new Map();

        base.forEach((item) => {
            const code = Number(item?.status_code);
            if (!Number.isFinite(code)) return;
            map.set(code, {
                status_code: code,
                status_name: item?.status_name || item?.status_text || item?.name || `Status ${code}`,
            });
        });

        // Senior Region Role (Quality/Operation/Technical Head) can approve leaves ≤4 days only
        // COO can approve ALL leaves (highest authority)
        // For leaves >4 days, only HR/FinalApprover/COO can approve
        const seniorRegionCanApprove = isSeniorRegionRole && leaveDays <= 4;
        const cooCanApprove = isCooRole; // COO can approve any leave
        const canApprove = isFinalApprover || isHrRole || seniorRegionCanApprove || cooCanApprove;
        const canRecommend = isBranchHeadRole || isClusterHeadRole || isSeniorRegionRole || isRegionRole;

        if (canRecommend && !map.has(STATUS_RECOMMENDED)) {
            map.set(STATUS_RECOMMENDED, {
                status_code: STATUS_RECOMMENDED,
                status_name: "Recommended",
            });
        }

        if (canApprove && !map.has(STATUS_APPROVED)) {
            map.set(STATUS_APPROVED, {
                status_code: STATUS_APPROVED,
                status_name: "Approved",
            });
        }

        if (!map.has(STATUS_PENDING)) {
            map.set(STATUS_PENDING, { status_code: STATUS_PENDING, status_name: "Pending" });
        }

        if (!map.has(STATUS_CANCELLED)) {
            map.set(STATUS_CANCELLED, { status_code: STATUS_CANCELLED, status_name: "Cancelled" });
        }

        const ordered = [STATUS_PENDING, STATUS_RECOMMENDED, STATUS_APPROVED, STATUS_CANCELLED]
            .filter((code) => map.has(code))
            .map((code) => map.get(code));

        return ordered;
    };

    const closeimagemodals = (event) => {
        if (event?.preventDefault) {
            event.preventDefault();
        }
        setShows(false);
        setShowRecommendPopup(false);
        setRecommendToStaffId("");
        setRecommendTargets([]);
        setActiveLeaveItem(null);
        setddchrleave_id("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!status) {
            Swal.fire("Warning", "Please select status", "warning");
            return;
        }

        const selectedStatus = Number(status);
        const selectedRecommendId = String(
            recommendToStaffId || selectedRecommendTargetFallback?.staff_id || ""
        );
        const selectedTarget =
            recommendTargets.find((item) => String(item.staff_id) === selectedRecommendId) ||
            (selectedRecommendTargetFallback &&
            String(selectedRecommendTargetFallback.staff_id) === selectedRecommendId
                ? selectedRecommendTargetFallback
                : null);

        if (selectedStatus === STATUS_RECOMMENDED && !selectedRecommendId) {
            Swal.fire("Warning", "Please select staff to recommend", "warning");
            return;
        }

        const currentLeaveDays = getLeaveDays(activeLeaveItem || {});
        // COO can approve ALL leaves (highest authority)
        const seniorRegionCanApprove = isSeniorRegionRole && currentLeaveDays <= 4;
        const cooCanApprove = isCooRole; // COO can approve any leave
        const canApproveNow = isHrRole || isFinalApprover || seniorRegionCanApprove || cooCanApprove;
        if (selectedStatus === STATUS_APPROVED && !canApproveNow) {
            Swal.fire(
                "Warning",
                "You do not have permission to approve this leave.",
                "warning"
            );
            return;
        }

        const payload = {
            ddchrleave_status: selectedStatus,
            approver_staffid: Number(loginStaffId),
            recommended_to_staffid:
                selectedStatus === STATUS_RECOMMENDED ? Number(selectedRecommendId) : null,
            hierarchy_staffname: selectedTarget?.staff_name || "",
            hierarchy_uptdby:
                selectedStatus === STATUS_RECOMMENDED ? Number(selectedRecommendId) : null,
            hierarchy_crtdby: Number(loginStaffId),
            approver_branchid: Number(scopeBranchId || 0) || null,
            approver_branchname: scopeBranch?.branch_name || "",
            approver_regionid: Number(scopeRegionId || 0) || null,
            approver_regionname: scopeRegion?.region_name || "",
            approver_levelid: currentApproverLevel?.Llevel_id ? Number(currentApproverLevel.Llevel_id) : null,
            approver_levelname: currentApproverLevelName || "",
            leave_days: currentLeaveDays,
            leave_branchid: getItemBranchId(activeLeaveItem || {}) || null,
            leave_branchname: getItemBranchName(activeLeaveItem || {}) || "",
        };

        try {
            const res = await axios.post(
                `${baseurladdress}updateleave/${ddchrleave_id}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${accesstoken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (res.data.status === "updated") {
                Swal.fire({
                    icon: "success",
                    title: res.data.message || "Updated successfully",
                    timer: 1200,
                    showConfirmButton: false,
                });

                setShows(false);
                setRecommendToStaffId("");
                setRecommendTargets([]);
                setActiveLeaveItem(null);
                await fetchLeaveList();
            } else {
                Swal.fire("Error", res.data.message || "Update failed", "error");
            }
        } catch (error) {
            Swal.fire(
                "Error",
                error?.response?.data?.message || "Server error while updating leave",
                "error"
            );
        }
    };

    const Editleave = async (item) => {
        if (!metadataLoaded || staffDirectory.length === 0 || !currentStaffRecord) {
            Swal.fire({
                icon: "info",
                title: "Please wait",
                text: "Loading staff information...",
                timer: 1000,
                showConfirmButton: false,
            });
            return;
        }

        setShows(true);
        setRecommendToStaffId("");
        setRecommendTargets([]);
        setActiveLeaveItem(item);
        setstaffname(item.ddchrleave_staffname || "");
        setddchrleave_id(item.ddchrleave_id);
        setleavedate(
            item.ddchrleave_appliedfordate
                ? Moment(item.ddchrleave_appliedfordate).format("YYYY-MM-DD")
                : ""
        );
        setleavedateto(
            item.ddchrleave_appliedtodate
                ? Moment(item.ddchrleave_appliedtodate).format("YYYY-MM-DD")
                : ""
        );
        setreason(item.ddchrleave_description || "");

        const initialStatus = Number(
            item.viewer_status ?? item.ddchrleave_status ?? STATUS_PENDING
        );
        setstatus(initialStatus === STATUS_RECOMMENDED ? "" : String(initialStatus));

        try {
            const res = await axios.get(
                `${baseurladdress}getrecommendtargets/${item.ddchrleave_id}/${loginStaffId}`,
                { headers: { Authorization: `Bearer ${accesstoken}` } }
            );

            const targets = Array.isArray(res.data?.response) ? res.data.response : [];
            
            // Get the next role patterns based on current user's role (matches backend logic)
            const isLongLeave = getLeaveDays(item) > 4;
            const nextRolePatterns = getNextRecommendationRolePatterns(currentApproverLevelName, isLongLeave);
            
            console.log("=== Editleave API Response ===");
            console.log("Current role:", currentApproverLevelName);
            console.log("Next role patterns:", nextRolePatterns);
            console.log("All targets from API:", targets.map(t => ({ 
                id: t.staff_id, 
                name: t.staff_name, 
                role: t.staff_level_name || t.staff_level_rolename || t.role_name 
            })));
            
                    // Filter targets to show only the next level approvers (matches backend getrecommendtargets logic)
            const nextApprovers = targets
                .filter((target) => {
                    const roleName = normalizeText(
                        target?.staff_level_name || 
                        target?.staff_level_rolename || 
                        target?.level_name || 
                        target?.role_name || 
                        ""
                    );
                    
                    const staffName = target?.staff_name || target?.LApr_staffname || "";
                    
                    // Check if this target's role matches any of the next role patterns
                    const matchesNextRole = nextRolePatterns.some(pattern => {
                        const normalizedPattern = normalizeText(pattern);
                        
                        // Direct match first
                        if (roleName.includes(normalizedPattern)) {
                            return true;
                        }
                        
                        // For region-level roles, match by base keyword (quality/operation/technical)
                        // This allows "quality head" pattern to match both "Quality Head" and "Quality Manager"
                        const baseKeyword = normalizedPattern
                            .replace(/head/g, "")
                            .replace(/manager/g, "")
                            .replace(/operations/g, "operation")
                            .trim();
                        
                        if (baseKeyword && (normalizedPattern.includes("head") || normalizedPattern.includes("manager"))) {
                            return roleName.includes(baseKeyword);
                        }
                        
                        return false;
                    });
                    
                    if (!matchesNextRole) {
                        console.log(`  Filtering out ${staffName} (${roleName}) - doesn't match next role patterns`);
                    }
                    
                    return matchesNextRole;
                })
                .filter((target) => String(target?.staff_id || "") !== String(loginStaffId))
                .sort((a, b) => {
                    const aPriority = resolveTargetLevelPriority(a);
                    const bPriority = resolveTargetLevelPriority(b);
                    if (aPriority !== bPriority) return aPriority - bPriority;
                    return normalizeText(a?.staff_name).localeCompare(normalizeText(b?.staff_name));
                });
            
            console.log("Filtered next approvers:", nextApprovers.map(t => ({ 
                id: t.staff_id, 
                name: t.staff_name, 
                role: t.staff_level_name || t.staff_level_rolename || t.role_name 
            })));
            console.log("=== End Editleave API Response ===");
            
            setRecommendTargets(nextApprovers);
            setRecommendToStaffId(String(nextApprovers[0]?.staff_id || ""));
        } catch (error) {
            console.error("Failed to load recommend targets:", error);
            // Fallback: filter local staff directory for cluster heads only
            const localClusterHeads = resolveLocalRecommendTargets(item).filter((target) => {
                const roleName = normalizeText(target?.staff_level_name || "");
                return roleName.includes("cluster head") || roleName.includes("department head");
            });
            setRecommendTargets(localClusterHeads);
            setRecommendToStaffId(String(localClusterHeads[0]?.staff_id || ""));
        }
    };

    const filteredByBranchData = useMemo(() => {
        if (!Array.isArray(selectedBranchIds) || selectedBranchIds.length === 0) {
            return scopedData;
        }

        const selectedBranchNames = selectedBranchIds
            .map((id) => normalizeText(branchLookup.get(String(id))?.branch_name))
            .filter(Boolean);

        return scopedData.filter((item) => {
            const itemBranchId = String(getItemBranchId(item) || "");
            const itemBranchName = normalizeText(getItemBranchName(item));

            if (itemBranchId && selectedBranchIds.includes(itemBranchId)) {
                return true;
            }

            if (selectedBranchNames.length > 0 && itemBranchName && selectedBranchNames.includes(itemBranchName)) {
                return true;
            }

            if (isBranchHeadRole && currentBranch?.branch_name) {
                return itemBranchName === normalizeText(currentBranch.branch_name);
            }

            return false;
        });
    }, [branchLookup, currentBranch, isBranchHeadRole, scopedData, selectedBranchIds]);

    const filteredData = useMemo(() => {
        return filteredByBranchData.filter((item) =>
            item.ddchrleave_staffname?.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, filteredByBranchData]);

    useEffect(() => {
        setNoDataFound(!loading && filteredData.length === 0);
    }, [loading, filteredData.length]);

    // Initialize DataTable
    useEffect(() => {
        if (!loading && filteredData.length > 0 && tableRef.current && !dataTableInitialized.current) {
            setTimeout(() => {
                if (tableRef.current && !dataTableInitialized.current) {
                    try {
                        if ($.fn.DataTable.isDataTable(tableRef.current)) {
                            $(tableRef.current).DataTable().destroy();
                        }
                        $(tableRef.current).DataTable({
                            paging: true,
                            pageLength: 10,
                            lengthMenu: [[10, 25, 50, -1], [10, 25, 50, "All"]],
                            searching: true,
                            ordering: true,
                            info: true,
                            autoWidth: false,
                            language: {
                                search: "Search:",
                                lengthMenu: "Show _MENU_ entries",
                                info: "Showing _START_ to _END_ of _TOTAL_ entries",
                                infoEmpty: "Showing 0 to 0 of 0 entries",
                                infoFiltered: "(filtered from _MAX_ total entries)",
                                paginate: {
                                    first: "First",
                                    last: "Last",
                                    next: "Next",
                                    previous: "Prev"
                                }
                            }
                        });
                        dataTableInitialized.current = true;
                    } catch (e) {
                        console.log("DataTable init error:", e);
                    }
                }
            }, 100);
        }
        if (loading || filteredData.length === 0) {
            if (dataTableInitialized.current && tableRef.current) {
                try {
                    if ($.fn.DataTable.isDataTable(tableRef.current)) {
                        $(tableRef.current).DataTable().destroy();
                    }
                } catch (e) {}
                dataTableInitialized.current = false;
            }
        }
    }, [loading, filteredData.length]);

    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            if (!sortConfig.key) return 0;

            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === "ddchrleave_appliedfordate") {
                aValue = new Date(aValue || 0).getTime();
                bValue = new Date(bValue || 0).getTime();
            } else {
                if (typeof aValue === "string") aValue = aValue.toLowerCase();
                if (typeof bValue === "string") bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig]);

    const totalPages = Math.max(Math.ceil(sortedData.length / pageSize), 1);
    const paginatedData = sortedData.slice(page * pageSize, (page + 1) * pageSize);

    const requestSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    useEffect(() => {
        if (!showBranchSelector && currentStaffBranchId && !selectedBranchIds.includes(currentStaffBranchId)) {
            setSelectedBranchIds([String(currentStaffBranchId)]);
            return;
        }

        if (branchOptions.length === 0) {
            if (selectedBranchIds.length > 0) {
                setSelectedBranchIds([]);
            }
            return;
        }

        if (selectedBranchIds.length === 0) {
            return;
        }

        const validBranchIds = branchOptions
            .map((item) => String(item?.branch_id || ""))
            .filter(Boolean);

        const selectedStillValid = selectedBranchIds.every((id) => validBranchIds.includes(id));

        if (!selectedStillValid) {
            setSelectedBranchIds([]);
        }
    }, [branchOptions, currentStaffBranchId, selectedBranchIds, showBranchSelector]);

    useEffect(() => {
        if (Number(status) !== STATUS_RECOMMENDED) {
            setRecommendToStaffId("");
            setShowRecommendPopup(false);
            return;
        }

        if (recommendTargets.length > 0) {
            setShowRecommendPopup(true);
        }
    }, [recommendTargets.length, status]);

    const modalStatusOptions = useMemo(() => {
        const currentLeaveDays = getLeaveDays(activeLeaveItem || {});
        const isLongLeave = currentLeaveDays > 4;
        // COO can approve ALL leaves (highest authority)
        const seniorRegionCanApprove = isSeniorRegionRole && currentLeaveDays <= 4;
        const cooCanApprove = isCooRole; // COO can approve any leave
        const canApprove = isHrRole || isFinalApprover || seniorRegionCanApprove || cooCanApprove;
        const canRecommend = isLongLeave
            ? (isBranchHeadRole || isClusterHeadRole || isSeniorRegionRole || isRegionRole)
            : (isBranchHeadRole || isClusterHeadRole);
        const base = buildStatusOptions(currentLeaveDays);

        return base.filter((item) => {
            const code = Number(item.status_code);

            if (code === STATUS_APPROVED) return canApprove;
            if (code === STATUS_RECOMMENDED) return canRecommend;
            return true;
        });
    }, [activeLeaveItem, approvalLevels, buildStatusOptions, currentApproverLevelName, isBranchHeadRole, isClusterHeadRole, isFinalApprover, isHrRole, isRegionRole, isSeniorRegionRole, statusOptions]);

    const selectedRecommendTarget = recommendTargets.find(
        (item) => String(item.staff_id) === String(recommendToStaffId)
    );
    const selectedRecommendTargetFallback = useMemo(() => {
        if (selectedRecommendTarget) {
            return selectedRecommendTarget;
        }

        if (Array.isArray(recommendTargets) && recommendTargets.length > 0) {
            return recommendTargets[0];
        }
        return null;
    }, [recommendTargets, selectedRecommendTarget]);
    const effectiveRecommendToStaffId = String(
        recommendToStaffId || selectedRecommendTargetFallback?.staff_id || ""
    );

    const currentScopeLabel = useMemo(() => {
        const parts = [];
        if (scopeBranch?.branch_name) parts.push(scopeBranch.branch_name);
        if (scopeRegion?.region_name) parts.push(scopeRegion.region_name);
        if (currentApproverLevelName) parts.push(currentApproverLevelName);
        return parts.join(" | ");
    }, [currentApproverLevelName, scopeBranch, scopeRegion]);

    // Bulk selection handlers - only Pending items can be selected
    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = paginatedData
                .filter((item) => {
                    const statusCode = Number(item?.viewer_status ?? item?.ddchrleave_status);
                    return statusCode === STATUS_PENDING;
                })
                .map((item) => String(item.ddchrleave_id));
            setSelectedLeaveIds(allIds);
        } else {
            setSelectedLeaveIds([]);
        }
    };

    const handleSelectItem = (id, checked) => {
        if (checked) {
            setSelectedLeaveIds((prev) => [...prev, String(id)]);
        } else {
            setSelectedLeaveIds((prev) => prev.filter((item) => item !== String(id)));
        }
    };

    const isAllSelected = useMemo(() => {
        const selectableItemsOnPage = paginatedData.filter((item) => {
            const statusCode = Number(item?.viewer_status ?? item?.ddchrleave_status);
            return statusCode === STATUS_PENDING;
        });
        
        if (selectableItemsOnPage.length === 0) {
            return false;
        }
        
        const selectableIdsOnPage = selectableItemsOnPage.map((item) => String(item.ddchrleave_id));
        const allSelectedOnPage = selectableIdsOnPage.every((id) => selectedLeaveIds.includes(id));
        
        // Return true only if all selectable items on this page are selected
        return allSelectedOnPage;
    }, [paginatedData, selectedLeaveIds]);

    const selectedItems = useMemo(() => {
        return sortedData.filter((item) => selectedLeaveIds.includes(String(item.ddchrleave_id)));
    }, [sortedData, selectedLeaveIds]);

    // Bulk status options for the slide panel
    const bulkStatusOptions = useMemo(() => {
        const isLongLeave = selectedItems.some((item) => getLeaveDays(item) > 4);
        const maxLeaveDays = selectedItems.length > 0 ? Math.max(...selectedItems.map(item => getLeaveDays(item))) : 0;
        // COO can approve ALL leaves (highest authority)
        const seniorRegionCanApprove = isSeniorRegionRole && maxLeaveDays <= 4;
        const cooCanApprove = isCooRole; // COO can approve any leave
        const canApprove = isHrRole || isFinalApprover || seniorRegionCanApprove || cooCanApprove;
        const canRecommend = isBranchHeadRole || isClusterHeadRole || isSeniorRegionRole || isRegionRole;
        const base = buildStatusOptions(maxLeaveDays);

        return base.filter((item) => {
            const code = Number(item.status_code);
            if (code === STATUS_APPROVED) return canApprove;
            if (code === STATUS_RECOMMENDED) return canRecommend;
            return true;
        });
    }, [selectedItems, isBranchHeadRole, isClusterHeadRole, isFinalApprover, isHrRole, isRegionRole, isSeniorRegionRole, statusOptions, approvalLevels, buildStatusOptions, currentApproverLevelName]);

    // Load bulk recommend targets when status changes to recommended
    useEffect(() => {
        if (Number(bulkStatus) !== STATUS_RECOMMENDED) {
            setBulkRecommendToStaffId("");
            setBulkRecommendTargets([]);
            return;
        }

        // Use the first selected item to determine recommend targets via API (same as modal edit)
        const loadBulkTargets = async () => {
            if (selectedItems.length > 0) {
                const firstItem = selectedItems[0];
                try {
                    const res = await axios.get(
                        `${baseurladdress}getrecommendtargets/${firstItem.ddchrleave_id}/${loginStaffId}`,
                        { headers: { Authorization: `Bearer ${accesstoken}` } }
                    );

                    const targets = Array.isArray(res.data?.response) ? res.data.response : [];
                    
                    // Get the next role patterns based on current user's role (matches backend logic)
                    const isLongLeave = getLeaveDays(firstItem) > 4;
                    const nextRolePatterns = getNextRecommendationRolePatterns(currentApproverLevelName, isLongLeave);
                    
                    console.log("=== Bulk recommend targets (API) ===");
                    console.log("Current role:", currentApproverLevelName);
                    console.log("Next role patterns:", nextRolePatterns);
                    console.log("All targets from API:", targets.map(t => ({ 
                        id: t.staff_id, 
                        name: t.staff_name, 
                        role: t.staff_level_name || t.staff_level_rolename || t.role_name 
                    })));
                    
                    // Filter targets to show only the next level approvers (matches backend getrecommendtargets logic)
                    const nextApprovers = targets
                        .filter((target) => {
                            const roleName = normalizeText(
                                target?.staff_level_name || 
                                target?.staff_level_rolename || 
                                target?.level_name || 
                                target?.role_name || 
                                ""
                            );
                            
                            // Check if this target's role matches any of the next role patterns
                            const matchesNextRole = nextRolePatterns.some(pattern => {
                                const normalizedPattern = normalizeText(pattern);
                                
                                // Direct match first
                                if (roleName.includes(normalizedPattern)) {
                                    return true;
                                }
                                
                                // For region-level roles, match by base keyword (quality/operation/technical)
                                const baseKeyword = normalizedPattern
                                    .replace(/head/g, "")
                                    .replace(/manager/g, "")
                                    .replace(/operations/g, "operation")
                                    .trim();
                                
                                if (baseKeyword && (normalizedPattern.includes("head") || normalizedPattern.includes("manager"))) {
                                    return roleName.includes(baseKeyword);
                                }
                                
                                return false;
                            });
                            
                            if (!matchesNextRole) {
                                console.log(`  Filtering out ${target.staff_name} (${roleName}) - doesn't match next role patterns`);
                            }
                            
                            return matchesNextRole;
                        })
                        .filter((target) => String(target?.staff_id || "") !== String(loginStaffId))
                        .sort((a, b) => {
                            const aPriority = resolveTargetLevelPriority(a);
                            const bPriority = resolveTargetLevelPriority(b);
                            if (aPriority !== bPriority) return aPriority - bPriority;
                            return normalizeText(a?.staff_name).localeCompare(normalizeText(b?.staff_name));
                        });
                    
                    console.log("Filtered next approvers:", nextApprovers.map(t => ({ 
                        id: t.staff_id, 
                        name: t.staff_name, 
                        role: t.staff_level_name || t.staff_level_rolename || t.role_name 
                    })));
                    console.log("=== End Bulk recommend targets (API) ===");
                    
                    setBulkRecommendTargets(nextApprovers);
                    if (nextApprovers.length > 0) {
                        setBulkRecommendToStaffId(String(nextApprovers[0].staff_id));
                    }
                } catch (error) {
                    console.error("Failed to load bulk recommend targets:", error);
                    setBulkRecommendTargets([]);
                    setBulkRecommendToStaffId("");
                }
            }
        };

        loadBulkTargets();
    }, [bulkStatus, selectedItems, currentApproverLevelName, loginStaffId, accesstoken]);

    const handleBulkSubmit = async () => {
        if (!bulkStatus) {
            Swal.fire("Warning", "Please select status", "warning");
            return;
        }

        if (selectedLeaveIds.length === 0) {
            Swal.fire("Warning", "No items selected", "warning");
            return;
        }

        const selectedStatus = Number(bulkStatus);
        if (selectedStatus === STATUS_RECOMMENDED && !bulkRecommendToStaffId) {
            Swal.fire("Warning", "Please select staff to recommend", "warning");
            return;
        }

        const confirmResult = await Swal.fire({
            title: "Confirm Bulk Update",
            text: `You are about to update ${selectedLeaveIds.length} leave request(s). Continue?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, proceed",
            cancelButtonText: "Cancel",
        });

        if (!confirmResult.isConfirmed) return;

        setBulkSubmitting(true);
        let successCount = 0;
        let failCount = 0;

        for (const leaveId of selectedLeaveIds) {
            const item = sortedData.find((i) => String(i.ddchrleave_id) === leaveId);
            if (!item) continue;

            const currentLeaveDays = getLeaveDays(item);
            // COO can approve ALL leaves (highest authority)
            const seniorRegionCanApprove = isSeniorRegionRole && currentLeaveDays <= 4;
            const cooCanApprove = isCooRole; // COO can approve any leave
            const canApproveNow = isHrRole || isFinalApprover || seniorRegionCanApprove || cooCanApprove;
            if (selectedStatus === STATUS_APPROVED && !canApproveNow) {
                continue;
            }

            const payload = {
                ddchrleave_status: selectedStatus,
                approver_staffid: Number(loginStaffId),
                recommended_to_staffid:
                    selectedStatus === STATUS_RECOMMENDED ? Number(bulkRecommendToStaffId) : null,
                hierarchy_staffname: "",
                hierarchy_uptdby:
                    selectedStatus === STATUS_RECOMMENDED ? Number(bulkRecommendToStaffId) : null,
                hierarchy_crtdby: Number(loginStaffId),
                approver_branchid: Number(scopeBranchId || 0) || null,
                approver_branchname: scopeBranch?.branch_name || "",
                approver_regionid: Number(scopeRegionId || 0) || null,
                approver_regionname: scopeRegion?.region_name || "",
                approver_levelid: currentApproverLevel?.Llevel_id ? Number(currentApproverLevel.Llevel_id) : null,
                approver_levelname: currentApproverLevelName || "",
                leave_days: currentLeaveDays,
                leave_branchid: getItemBranchId(item) || null,
                leave_branchname: getItemBranchName(item) || "",
            };

            try {
                const res = await axios.post(
                    `${baseurladdress}updateleave/${leaveId}`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${accesstoken}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (res.data.status === "updated") {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (error) {
                console.error(`Failed to update leave ${leaveId}:`, error);
                failCount++;
            }
        }

        setBulkSubmitting(false);

        if (successCount > 0) {
            Swal.fire({
                icon: "success",
                title: `Updated ${successCount} leave(s) successfully`,
                timer: 1500,
                showConfirmButton: false,
            });
            setSelectedLeaveIds([]);
            setBulkStatus("");
            setBulkRecommendToStaffId("");
            setBulkRecommendTargets([]);
            await fetchLeaveList();
        } else {
            Swal.fire("Error", "Failed to update any leaves", "error");
        }
    };

    const clearBulkSelection = () => {
        setSelectedLeaveIds([]);
        setBulkStatus("");
        setBulkRecommendToStaffId("");
        setBulkRecommendTargets([]);
    };

    return (
        <>
            <Sidennav />
            <Topnavbar />
            <div className="content-wrap">
                <div className="main">
                    <div className="container-fluid">
                        <div className="col-lg-12">
                            <div className="card">
                                <div
                                    className="card-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2"
                                    style={{ color: "black" }}
                                >
                                    <div>
                                        <h6 className="card-title mb-1">Leave Details</h6>
                                        <small className="text-muted">
                                            Scope: {currentScopeLabel || "No approver scope found"}
                                            {isHrRole ? " | HR can view this region" : ""}
                                        </small>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {showBranchSelector ? (
                                            <div style={branchSelectorStyles.wrapper} ref={dropdownRef}>
                                                <div
                                                    style={branchSelectorStyles.trigger}
                                                    onClick={() => setShowBranchDropdown((prev) => !prev)}
                                                >
                                                    <span style={branchSelectorStyles.triggerText}>
                                                        {selectedBranchIds.length === 0
                                                            ? "Choose Branches"
                                                            : selectedBranchIds.length === branchOptions.length && branchOptions.length > 0
                                                                ? "All Branches Selected"
                                                                : `${selectedBranchIds.length} Branch${selectedBranchIds.length > 1 ? "es" : ""} Selected`
                                                        }
                                                    </span>
                                                    <i
                                                        className={`fa ${showBranchDropdown ? "fa-chevron-up" : "fa-chevron-down"}`}
                                                        style={branchSelectorStyles.triggerIcon}
                                                    ></i>
                                                </div>

                                                {showBranchDropdown && (
                                                    <div style={branchSelectorStyles.dropdown}>
                                                        <div style={branchSelectorStyles.header}>
                                                            <span style={branchSelectorStyles.headerText}>Choose Branches</span>
                                                        </div>

                                                        <div style={branchSelectorStyles.list}>
                                                            <label
                                                                htmlFor="select-all-branches-list"
                                                                style={{
                                                                    ...branchSelectorStyles.item,
                                                                    backgroundColor: branchOptions.length > 0 && selectedBranchIds.length === branchOptions.length ? "#edf3ff" : "#f8faff",
                                                                    border: branchOptions.length > 0 && selectedBranchIds.length === branchOptions.length ? "1px solid #bfd2ff" : "1px solid #edf1f7"
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    id="select-all-branches-list"
                                                                    checked={branchOptions.length > 0 && selectedBranchIds.length === branchOptions.length}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedBranchIds(branchOptions.map((b) => String(b.branch_id)));
                                                                        } else {
                                                                            setSelectedBranchIds([]);
                                                                        }
                                                                    }}
                                                                    style={branchSelectorStyles.checkbox}
                                                                />
                                                                <span style={{ ...branchSelectorStyles.label, fontWeight: 700 }}>
                                                                    Select All Branches
                                                                </span>
                                                            </label>

                                                            {branchOptions.map((item) => {
                                                                const branchCode = getBranchCode(item);
                                                                const branchId = getBranchId(item);
                                                                const isChecked = selectedBranchIds.includes(String(branchId));
                                                                const regionName = regionLookup.get(String(item.branch_region))?.region_name;

                                                                return (
                                                                    <label
                                                                        htmlFor={`branch-list-${branchId}`}
                                                                        key={`${branchId}-${branchCode}`}
                                                                        style={{
                                                                            ...branchSelectorStyles.item,
                                                                            backgroundColor: isChecked ? "#edf3ff" : "#ffffff",
                                                                            border: isChecked ? "1px solid #bfd2ff" : "1px solid #edf1f7"
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`branch-list-${branchId}`}
                                                                            checked={isChecked}
                                                                            onChange={() => {
                                                                                setSelectedBranchIds((prev) =>
                                                                                    isChecked
                                                                                        ? prev.filter((id) => id !== String(branchId))
                                                                                        : [...prev, String(branchId)]
                                                                                );
                                                                            }}
                                                                            style={branchSelectorStyles.checkbox}
                                                                        />
                                                                        <span style={branchSelectorStyles.label}>
                                                                            {getBranchName(item)}
                                                                            {regionName ? ` (${regionName})` : ""}
                                                                        </span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            scopeBranch?.branch_name && (
                                                <small className="text-muted me-2">
                                                    Branch: {scopeBranch.branch_name}
                                                </small>
                                            )
                                        )}
                                        <label className="mb-0">From:</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm w-auto"
                                            value={periodFrom}
                                            onChange={(e) => setPeriodFrom(e.target.value)}
                                        />
                                        <label className="mb-0">To:</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm w-auto"
                                            value={periodTo}
                                            onChange={(e) => setPeriodTo(e.target.value)}
                                        />
                                    </div>

                                    <input
                                        type="text"
                                        className="form-control form-control-sm w-auto"
                                        placeholder="Search by Staff Name"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table ref={tableRef} className="table table-bordered table-responsive-sm" id="leaveListTable">
                                                <thead>
                                                    <tr style={{ textAlign: "left" }}>
                                                        {isBranchHeadRole && (
                                                            <th style={{ width: "40px" }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isAllSelected}
                                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                                    style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                                                />
                                                            </th>
                                                        )}
                                                        <th
                                                            onClick={() => requestSort("ddchrleave_staffname")}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            Staff Name
                                                        </th>
                                                        <th
                                                            onClick={() => requestSort("ddchrleave_appliedfordate")}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            Leave Date
                                                        </th>
                                                        <th>Branch / Region</th>
                                                        <th>Reason for Leave</th>
                                                        <th style={{ width: "20%", whiteSpace: "nowrap" }}>Status</th>
                                                        <th>Edit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedData.length > 0 ? (
                                                        paginatedData.map((item, index) => {
                                                            const itemBranchId = String(getItemBranchId(item) || "");
                                                            const itemBranch = branchLookup.get(itemBranchId);
                                                            const itemRegionId = String(
                                                                pickFirst(itemBranch?.branch_region, item?.branch_region, "") || ""
                                                            );
                                                            const itemRegion = regionLookup.get(itemRegionId);
                                                            const leaveDays = getLeaveDays(item);
                                                            const statusCode = Number(
                                                                item?.viewer_status ?? item?.ddchrleave_status
                                                            );
                                                            const isCurrentRecommender =
                                                                String(
                                                                    pickFirst(
                                                                        item.last_recommender_staffid,
                                                                        item.approver_staffid
                                                                    ) || ""
                                                                ) ===
                                                                String(loginStaffId || "");
                                                            const recipientLevel = findApprovalLevelById(
                                                                approvalLevels,
                                                                getRecipientLevelId(item)
                                                            );
                                                            const isCurrentRecipient =
                                                                String(getRecipientStaffId(item) || "") ===
                                                                String(loginStaffId || "") ||
                                                                String(getRecipientLevelId(item) || "") ===
                                                                String(currentApproverLevel?.Llevel_id || "") ||
                                                                normalizeText(getRecipientLevelName(item)) ===
                                                                normalizeText(currentApproverLevelName) ||
                                                                normalizeText(getRecipientLevelName(item)) ===
                                                                normalizeText(currentRoleText) ||
                                                                normalizeText(
                                                                    pickFirst(
                                                                        item.last_recommended_to_staffname,
                                                                        item.recommended_to_staffname,
                                                                        item.hierarchy_staffname,
                                                                        item.recommended_staffname
                                                                    )
                                                                ) ===
                                                                normalizeText(
                                                                    pickFirst(
                                                                        currentStaffRecord?.staff_name,
                                                                        currentStaffRecord?.staff_fullname,
                                                                        currentStaffRecord?.username
                                                                    )
                                                                ) ||
                                                                resolveConfiguredApprovalPriority(
                                                                    approvalLevels,
                                                                    pickFirst(
                                                                        recipientLevel?.Llevel_rolename,
                                                                        item.last_recommended_to_level_name,
                                                                        item.recommended_to_level_name,
                                                                        item.hierarchy_levelname,
                                                                        item.recommended_level_name
                                                                    ),
                                                                    recipientLevel?.Llevel_priority
                                                                ) === currentApproverLevelPriority;


                                                            const isUnroutedLeave =
                                                                !getRecipientStaffId(item) &&
                                                                !getRecipientLevelId(item) &&
                                                                !getRecipientLevelName(item) &&
                                                                !pickFirst(item?.last_recommender_staffid, item?.approver_staffid);

                                                                // Permission rules:
                                                                // - COO: Can edit leaves >4 days from any staff OR ALL leaves from Quality/Technical/Operation heads
                                                                // - HR: Can edit all leaves in their region scope
                                                                // - Region/Technical/Operation/Quality Head: Can edit recommended leaves
                                                                // - Branch Head: Can edit their own branch leaves
                                                                const isCooEligible = isCooRole; // COO eligibility check
                                                                const hasEditPermission = isCooEligible || isHrRole || Number(item.can_edit) === 1;

                                                                const canEditRow =
                                                                    hasEditPermission &&
                                                                    (statusCode === STATUS_RECOMMENDED ||
                                                                        statusCode === STATUS_PENDING ||
                                                                        statusCode === STATUS_APPROVED ||
                                                                        statusCode === STATUS_CANCELLED) &&
                                                                    // COO can edit based on canCooApproveLeave rules, HR can edit all in region, others must be recipient/unrouted
                                                                    (canCooApproveLeave(item) || isHrRole || isSeniorRegionRole || isCurrentRecipient || isUnroutedLeave) &&
                                                                    // COO can edit even if they previously recommended
                                                                    // For others: cannot edit if already forwarded by them
                                                                    (!isCurrentRecommender || isCooEligible) &&
                                                                    // HR restriction: cannot edit if already forwarded by them
                                                                    (!isHrRole || !isCurrentRecommender);


                                                            const isSelectable = statusCode === STATUS_PENDING;
                                                            const isChecked = selectedLeaveIds.includes(String(item.ddchrleave_id));

                                                            return (
                                                                <tr key={item.ddchrleave_id} style={{ backgroundColor: isChecked ? "#f0f7ff" : "" }}>
                                                                    {isBranchHeadRole && (
                                                                        <td>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isChecked}
                                                                                disabled={!isSelectable}
                                                                                onChange={(e) => handleSelectItem(item.ddchrleave_id, e.target.checked)}
                                                                                style={{ width: "16px", height: "16px", cursor: isSelectable ? "pointer" : "not-allowed" }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    <td>
                                                                        {item.ddchrleave_staffname}
                                                                        <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                                            {leaveDays} day{leaveDays === 1 ? "" : "s"}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {item.ddchrleave_appliedfordate &&
                                                                            item.ddchrleave_appliedfordate !== "0000-00-00 00:00:00"
                                                                            ? Moment(item.ddchrleave_appliedfordate).format("DD-MM-YYYY")
                                                                            : ""}
                                                                        {" - "}
                                                                        {item.ddchrleave_appliedtodate &&
                                                                            item.ddchrleave_appliedtodate !== "0000-00-00 00:00:00"
                                                                            ? Moment(item.ddchrleave_appliedtodate).format("DD-MM-YYYY")
                                                                            : ""}
                                                                    </td>
                                                                    <td>
                                                                        <div>{itemBranch?.branch_name || getItemBranchName(item) || "-"}</div>
                                                                        <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                                            {itemRegion?.region_name || item?.region_name || "-"}
                                                                        </div>
                                                                    </td>
                                                                    <td>{item.ddchrleave_description}</td>
                                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                                        <div className="d-flex align-items-center flex-nowrap">
                                                                            {statusCode === STATUS_PENDING ? (
                                                                                <span className="badge bg-warning">Pending</span>
                                                                            ) : statusCode === STATUS_APPROVED ? (
                                                                                <span className="badge bg-success">Approved</span>
                                                                            ) : statusCode === STATUS_RECOMMENDED ? (
                                                                                <span className="badge bg-info">Recommended</span>
                                                                            ) : (
                                                                                <span className="badge bg-danger">Cancelled</span>
                                                                            )}
                                                                            {statusCode === STATUS_RECOMMENDED &&
                                                                                String(getRecipientStaffId(item) || "") !==
                                                                                String(loginStaffId || "") &&
                                                                                pickFirst(
                                                                                    item.last_recommended_to_staffname,
                                                                                    item.recommended_to_staffname,
                                                                                    item.hierarchy_staffname,
                                                                                    item.recommended_staffname
                                                                                ) && (
                                                                                    <small
                                                                                        className="text-muted"
                                                                                        style={{ marginLeft: "10px" }}
                                                                                    >
                                                                                        {pickFirst(
                                                                                            item.last_recommended_to_staffname,
                                                                                            item.recommended_to_staffname,
                                                                                            item.hierarchy_staffname,
                                                                                            item.recommended_staffname
                                                                                        )
                                                                                            ? `- ${pickFirst(
                                                                                                item.last_recommended_to_staffname,
                                                                                                item.recommended_to_staffname,
                                                                                                item.hierarchy_staffname,
                                                                                                item.recommended_staffname
                                                                                            )}`
                                                                                            : ""}
                                                                                        {pickFirst(
                                                                                            item.last_recommended_to_staffname,
                                                                                            item.recommended_to_staffname,
                                                                                            item.hierarchy_staffname,
                                                                                            item.recommended_staffname
                                                                                        ) &&
                                                                                            (item.last_recommended_to_level_name ||
                                                                                                item.recommended_to_level_name ||
                                                                                                item.hierarchy_levelname ||
                                                                                                item.recommended_level_name ||
                                                                                                item.last_recommended_to_level_id ||
                                                                                                item.recommended_to_level_id ||
                                                                                                item.hierarchy_levelid ||
                                                                                                item.recommended_level_id)
                                                                                            ? ` (${item.last_recommended_to_level_name ||
                                                                                            item.recommended_to_level_name ||
                                                                                            item.hierarchy_levelname ||
                                                                                            item.recommended_level_name ||
                                                                                            `Level ${item.last_recommended_to_level_id ||
                                                                                            item.recommended_to_level_id ||
                                                                                            item.hierarchy_levelid ||
                                                                                            item.recommended_level_id}`
                                                                                            })`
                                                                                            : ""}
                                                                                    </small>
                                                                                )}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <center>
                                                                            <i
                                                                                className={`fa fa-edit ${loading || !canEditRow ? "text-muted" : "text-primary"
                                                                                    }`}
                                                                                style={{
                                                                                    cursor: loading || !canEditRow ? "not-allowed" : "pointer",
                                                                                    opacity: loading || !canEditRow ? 0.5 : 1,
                                                                                }}
                                                                                onClick={() => {
                                                                                    if (loading || !canEditRow) return;
                                                                                    Editleave(item);
                                                                                }}
                                                                                title={
                                                                                    !canEditRow
                                                                                        ? "Already forwarded - only the recipient can edit"
                                                                                        : statusCode === STATUS_RECOMMENDED
                                                                                            ? "Edit recommended leave"
                                                                                            : "Edit"
                                                                                }
                                                                            />
                                                                        </center>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center text-muted">
                                                                {loading
                                                                    ? "Loading..."
                                                                    : noDataFound
                                                                        ? "No leave requests found for your branch, region, or approver scope."
                                                                        : "No matching records found."}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            {/* Compact Bulk Action Bar - Branch Head only */}
                           {/* ================= Floating Bottom Bulk Action Bar ================= */}
{isBranchHeadRole && selectedLeaveIds.length > 0 && (
    <div
        className="d-flex align-items-center justify-content-between flex-wrap"
        style={{
            position: "fixed",
            bottom: "70px", // Adjust height from bottom
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,

            // Your existing design
            backgroundColor: "#f5f9ff",
            border: "1px solid #d4e0f0",
            borderRadius: "6px",
            padding: "8px 14px",
            gap: "12px",

            // Floating Effect
            boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
            width: "fit-content",
            minWidth: "720px",
            maxWidth: "95%",
            animation: "bulkBarShow .25s ease-out",
        }}
    >
        <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "10px" }}
        >
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    backgroundColor: "#2f5bea",
                    color: "#fff",
                    borderRadius: "4px",
                    padding: "3px 10px",
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                }}
            >
                <i
                    className="fa fa-check-square-o"
                    style={{ fontSize: "0.7rem" }}
                ></i>
                {selectedLeaveIds.length} selected
            </span>

            <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                style={{
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "0.8rem",
                    color: "#2f3b52",
                    backgroundColor: "#fff",
                    width: "135px",
                    cursor: "pointer",
                    outline: "none",
                }}
            >
                <option value="">Status</option>

                {bulkStatusOptions.map((item) => (
                    <option
                        key={item.status_code}
                        value={item.status_code}
                    >
                        {item.status_name}
                    </option>
                ))}
            </select>

            {Number(bulkStatus) === STATUS_RECOMMENDED && (
                <select
                    value={bulkRecommendToStaffId}
                    onChange={(e) =>
                        setBulkRecommendToStaffId(e.target.value)
                    }
                    style={{
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                        color: "#2f3b52",
                        backgroundColor: "#fff",
                        width: "155px",
                        cursor: "pointer",
                        outline: "none",
                    }}
                >
                    <option value="">Recommend To</option>

                    {bulkRecommendTargets.map((target) => (
                        <option
                            key={target.staff_id}
                            value={target.staff_id}
                        >
                            {target.staff_name}
                            {target.staff_level_name
                                ? ` - ${target.staff_level_name}`
                                : ""}
                        </option>
                    ))}
                </select>
            )}
        </div>

        <div
            className="d-flex align-items-center"
            style={{ gap: "8px" }}
        >
            <button
                type="button"
                onClick={clearBulkSelection}
                style={{
                    border: "1px solid #dc3545",
                    borderRadius: "4px",
                    backgroundColor: "transparent",
                    color: "#dc3545",
                    padding: "4px 14px",
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#dc3545";
                    e.target.style.color = "#fff";
                }}
                onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#dc3545";
                }}
            >
                Cancel
            </button>

            <button
                type="button"
                onClick={handleBulkSubmit}
                disabled={
                    bulkSubmitting ||
                    !bulkStatus ||
                    (Number(bulkStatus) === STATUS_RECOMMENDED &&
                        !bulkRecommendToStaffId)
                }
                style={{
                    border: "1px solid #28a745",
                    borderRadius: "4px",
                    backgroundColor: "#28a745",
                    color: "#fff",
                    padding: "4px 14px",
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    cursor:
                        bulkSubmitting ||
                        !bulkStatus ||
                        (Number(bulkStatus) === STATUS_RECOMMENDED &&
                            !bulkRecommendToStaffId)
                            ? "not-allowed"
                            : "pointer",
                    opacity:
                        bulkSubmitting ||
                        !bulkStatus ||
                        (Number(bulkStatus) === STATUS_RECOMMENDED &&
                            !bulkRecommendToStaffId)
                            ? 0.6
                            : 1,
                    transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                    if (!e.target.disabled)
                        e.target.style.backgroundColor = "#218838";
                }}
                onMouseOut={(e) => {
                    if (!e.target.disabled)
                        e.target.style.backgroundColor = "#28a745";
                }}
            >
                {bulkSubmitting ? (
                    <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                    ></span>
                ) : (
                    "Update"
                )}
            </button>
        </div>
    </div>
)}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModals} size="xl">
                <Modal.Header >
                    <h5 className="mb-0">Edit Leave Application</h5>


                    <button
                        type="button"
                        className="btn btn-link p-0 text-dark text-decoration-none"
                        onClick={closeimagemodals}
                        aria-label="Close"
                        style={{ fontSize: "1.1rem", lineHeight: 1 }}
                    >
                        X
                    </button>






                </Modal.Header>
                <Modal.Body>
                    <form encType="multipart/form-data" onSubmit={handleSubmit}>
                        <div className="form-group row">
                            <div className="card-body">
                                <div className="basic-form">
                                    <div className="form-row">
                                        <div className="col-sm-12">
                                            <h6>Staff Name</h6>
                                            <input type="text" className="form-control" value={staffname} readOnly />
                                        </div>
                                        <div className="col-sm-12 mt-2">
                                            <h6>Reason for Leave</h6>
                                            <textarea className="form-control" value={reason} readOnly />
                                        </div>
                                        <div className="col-sm-6 mt-2">
                                            <h6>Date From</h6>
                                            <input type="date" className="form-control" value={leavedate} readOnly />
                                        </div>
                                        <div className="col-sm-6 mt-2">
                                            <h6>Date To</h6>
                                            <input type="date" className="form-control" value={leavedateto} readOnly />
                                        </div>
                                        <div className="col-sm-12 mt-2">
                                            <h6>Status</h6>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={status}
                                                onChange={(e) => setstatus(e.target.value)}
                                            >
                                                <option value="">Select Status</option>
                                                {modalStatusOptions.map((item) => (
                                                    <option key={item.status_code} value={item.status_code}>
                                                        {item.status_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {Number(status) === STATUS_RECOMMENDED && (
                                            <div className="col-sm-12 mt-2">
                                                <h6>Recommend To</h6>
                                                {!getItemBranchId(activeLeaveItem) ? (
                                                    <div className="alert alert-warning" role="alert">
                                                        <strong>Cannot Recommend:</strong> This leave request does not have a branch assigned. 
                                                        Please contact HR to update the leave record with the branch information before recommending.
                                                    </div>
                                                ) : recommendTargets.length === 0 ? (
                                                    <div className="alert alert-info" role="alert">
                                                        <strong>No Approvers Available:</strong> No {getNextRecommendationRolePatterns(currentApproverLevelName, getLeaveDays(activeLeaveItem || {}) > 4).join(" or ")} found for this branch.
                                                        Please ensure cluster heads are assigned to this branch.
                                                    </div>
                                                ) : (
                                                    <>
                                                        <select
                                                            className="form-control"
                                                            value={effectiveRecommendToStaffId}
                                                            onChange={(e) => setRecommendToStaffId(e.target.value)}
                                                            required={Number(status) === STATUS_RECOMMENDED}
                                                        >
                                                            {selectedRecommendTargetFallback &&
                                                                !recommendTargets.some(
                                                                    (item) =>
                                                                        String(item.staff_id) ===
                                                                        String(selectedRecommendTargetFallback.staff_id)
                                                                ) && (() => {
                                                                    const targetBranchName = resolveTargetBranchName(selectedRecommendTargetFallback);
                                                                    return (
                                                                        <option value={selectedRecommendTargetFallback.staff_id}>
                                                                            {selectedRecommendTargetFallback.staff_name}
                                                                            {selectedRecommendTargetFallback.staff_level_name ||
                                                                                selectedRecommendTargetFallback.staff_level_id
                                                                                ? ` - ${selectedRecommendTargetFallback.staff_level_name ||
                                                                                `Level ${selectedRecommendTargetFallback.staff_level_id}`
                                                                                }`
                                                                                : ""}
                                                                            {targetBranchName ? ` (${targetBranchName})` : ""}
                                                                        </option>
                                                                    );
                                                                })()}
                                                            <option value="">
                                                                {selectedRecommendTargetFallback?.staff_name
                                                                    ? `Select Staff (${selectedRecommendTargetFallback.staff_name})`
                                                                    : "Select Staff"}
                                                            </option>
                                                            {recommendTargets.map((item) => {
                                                                const targetBranchName = resolveTargetBranchName(item);
                                                                return (
                                                                    <option key={item.staff_id} value={item.staff_id}>
                                                                        {item.staff_name}
                                                                        {pickFirst(
                                                                            item.staff_level_name,
                                                                            item.staff_level_id
                                                                        )
                                                                            ? ` - ${item.staff_level_name || `Level ${item.staff_level_id}`}`
                                                                            : ""}
                                                                        {targetBranchName ? ` (${targetBranchName})` : ""}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                        {selectedRecommendTargetFallback && (
                                                            <small className="text-muted d-block mt-1">
                                                                Next recommendation: {selectedRecommendTargetFallback.staff_name}
                                                                {selectedRecommendTargetFallback.staff_level_name ||
                                                                    selectedRecommendTargetFallback.staff_level_id
                                                                    ? ` (${selectedRecommendTargetFallback.staff_level_name ||
                                                                    `Level ${selectedRecommendTargetFallback.staff_level_id}`
                                                                    })`
                                                                    : ""}
                                                            </small>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={closeimagemodals}>
                                Cancel
                            </Button>
                            <button
                                type="submit"
                                className="btn btn-success"
                                disabled={Number(status) === STATUS_RECOMMENDED && !effectiveRecommendToStaffId}
                                title={
                                    Number(status) === STATUS_RECOMMENDED && !effectiveRecommendToStaffId
                                        ? "Select Recommend To staff"
                                        : ""
                                }
                            >
                                Update
                            </button>
                        </Modal.Footer>
                    </form>
                </Modal.Body>
            </Modal>

            {/* <Modal show={showRecommendPopup} onHide={() => setShowRecommendPopup(false)} centered>
                <Modal.Header closeButton>
                    <h5 className="mb-0">Available Approvers</h5>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        These are the next valid staff members in the same branch for this leave route.
                    </p>
                    <div className="list-group">
                        {recommendTargets.map((item) => (
                            <button
                                type="button"
                                key={item.staff_id}
                                className={`list-group-item list-group-item-action text-start ${String(recommendToStaffId) === String(item.staff_id) ? "active" : ""
                                    }`}
                                onClick={() => {
                                    setRecommendToStaffId(String(item.staff_id));
                                    setShowRecommendPopup(false);
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>{item.staff_name}</span>
                                    <small>
                                        {pickFirst(item.staff_level_name, item.staff_level_id)
                                            ? item.staff_level_name || `Level ${item.staff_level_id}`
                                            : ""}
                                    </small>
                                </div>
                                {(item.branch_name || resolveTargetBranchName(item)) && (
                                    <small className="d-block text-muted">
                                        {item.branch_name || resolveTargetBranchName(item)}
                                    </small>
                                )}
                            </button>
                        ))}
                    </div>
                </Modal.Body>
            </Modal> */}
        </>
    );
}

export default Leavelist;
